import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

const faqs = [
  {
    q: '韩漫网站的漫画都是免费的吗？',
    a: '是的，韩漫网站上的所有韩国漫画均提供免费在线阅读服务。我们致力于为中文读者打造一个开放、无门槛的韩漫阅读平台，让每一位韩漫爱好者都能畅享高清无删减的漫画内容。',
  },
  {
    q: '网站上的韩漫是否是高清无删减版本？',
    a: '我们上架的每一部韩国漫画均经过严格筛选，确保为高清画质的无删减完整版本。下拉式阅读模式让您无需翻页，流畅体验原汁原味的韩漫内容。',
  },
  {
    q: '韩漫的更新频率是怎样的？',
    a: '我们拥有专业的韩漫内容更新团队，热门连载韩漫通常能在官方发布后24小时内完成同步更新。您可以通过网站首页的"最新更新"板块或关注特定漫画来获取更新通知。',
  },
  {
    q: '如何在手机上看韩漫？',
    a: '韩漫网站采用响应式设计，自动适配手机、平板和电脑屏幕。您只需在手机浏览器中打开我们的网站，即可享受与电脑端一致的高清下拉式韩漫阅读体验，无需下载任何App。',
  },
  {
    q: '遇到页面加载慢或打不开怎么办？',
    a: '建议您检查网络连接，或尝试清除浏览器缓存后刷新页面。如果问题持续存在，您可以通过页面下方的联系表单向我们反馈具体问题，我们会在第一时间为您排查解决。',
  },
  {
    q: '网站上的漫画分类有哪些？',
    a: '我们提供恋爱、奇幻、动作、剧情、搞笑、悬疑、恐怖、日常、古风、科幻等十大韩漫分类，您可以通过首页的分类入口或导航栏中的分类页面，快速找到自己喜欢的韩漫类型。',
  },
];

const testimonials = [
  {
    name: '小雨',
    avatar: 'ri-user-3-line',
    text: '这个网站真的太棒了！韩漫资源超全，画质也是高清的，每天都刷到停不下来。下拉式阅读体验比翻页舒服太多了！',
    rating: 5,
  },
  {
    name: '风之子',
    avatar: 'ri-user-3-line',
    text: '追《我独自升级》好久了，这里的更新速度真的快，基本上韩国那边更新后马上就能看到。推荐给了好多朋友！',
    rating: 5,
  },
  {
    name: '樱花落',
    avatar: 'ri-user-3-line',
    text: '分类做得很好，想找什么类型的韩漫都能快速定位。特别是恋爱类韩漫资源非常丰富，强烈推荐！',
    rating: 5,
  },
  {
    name: '墨染',
    avatar: 'ri-user-3-line',
    text: '用了快一年了，画质一直很稳定，无删减这点真的很良心。希望能一直做下去，支持！',
    rating: 5,
  },
];

const milestones = [
  { year: '2020', title: '网站创立', desc: '韩漫网站在韩国首尔成立，致力于将优质韩国漫画引入中文市场。' },
  { year: '2021', title: '用户突破百万', desc: '凭借高清画质和完整内容，月活跃用户突破100万。' },
  { year: '2022', title: '资源破万', desc: '网站收录韩国漫画资源突破10,000部，覆盖全品类。' },
  { year: '2023', title: '口碑爆发', desc: '成为中文地区最受欢迎的韩漫免费阅读平台之一，月活突破300万。' },
  { year: '2024', title: '体验升级', desc: '全面升级下拉式阅读体验，优化加载速度和画质输出。' },
  { year: '2025', title: '持续成长', desc: '月活用户突破500万，日均阅读量超千万次。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  // FAQ toggle states
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set());

  const toggleFaq = (index: number) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // SEO
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 韩漫网站',
      url: 'https://koreanmanhwa.site/about',
      description: '了解韩漫网站的品牌故事、用户口碑和联系信息。韩漫网站致力于提供高清无删减韩国漫画免费在线阅读服务。',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://koreanmanhwa.site/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://koreanmanhwa.site/about' },
        ],
      },
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };

    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '韩漫网站',
      url: 'https://koreanmanhwa.site/',
      description: '韩国漫画免费在线阅读平台',
      sameAs: ['https://koreanmanhwa.site/'],
    };

    document.querySelectorAll('script[data-seo="about"]').forEach((el) => el.remove());

    [schema, faqSchema, orgSchema].forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'about');
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    document.title = '关于我们 - 韩国漫画免费在线阅读_韩漫网站';

    return () => {
      document.querySelectorAll('script[data-seo="about"]').forEach((el) => el.remove());
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pds69n2trhb35gvdgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = (parsed as { meta?: { message?: string }; message?: string })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="py-12 md:py-20 text-center">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
              关于韩漫网站
            </h1>
            <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
              韩国漫画免费在线阅读平台，下拉式韩漫大全提供高清无删减韩漫全集，
              致力于为每一位韩漫爱好者打造极致的沉浸式阅读之旅。
            </p>
          </div>
        </section>

        {/* Brand story & milestones */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="max-w-[800px] mx-auto text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                我们的故事
              </h2>
              <p className="text-foreground-500 leading-relaxed">
                韩漫网站诞生于2020年，几位热爱韩国漫画的创始人在首尔相遇，他们发现中文地区的韩漫爱好者
                常常面临资源不全、画质模糊、翻译延迟等问题。于是他们决定打造一个专注于高清无删减韩漫
                免费阅读的平台，让每一位中文读者都能第一时间畅享原汁原味的韩国漫画内容。
              </p>
            </div>

            {/* Timeline */}
            <div className="relative max-w-[900px] mx-auto">
              {/* Vertical line */}
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block" />

              <div className="flex flex-col gap-6">
                {milestones.map((item, index) => (
                  <div key={item.year} className={`flex items-center gap-4 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                        <span className="text-xs text-primary-500 font-bold">{item.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-5 h-5 rounded-full bg-primary-500 border-4 border-background-100 flex-shrink-0 relative z-10" />
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {[
                { value: '10,000+', label: '韩漫资源', icon: 'ri-book-open-line' },
                { value: '500万+', label: '月活用户', icon: 'ri-user-heart-line' },
                { value: '99.9%', label: '高清画质', icon: 'ri-hd-line' },
                { value: '24h', label: '更新速度', icon: 'ri-timer-flash-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-accent-100">
                    <i className={`${stat.icon} text-xl md:text-2xl text-accent-600`} />
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                来自韩漫爱好者的真实评价
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.text}</p>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className={`${t.avatar} text-secondary-600 text-sm`} />
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                快速了解韩漫网站的使用方式
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <h4 className="text-sm font-medium text-foreground-900 pr-4">
                      {faq.q}
                    </h4>
                    <i className={`text-foreground-500 flex-shrink-0 transition-transform duration-200 ${openFaqs.has(index) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                  </button>
                  {openFaqs.has(index) && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact form */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[600px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                如有任何问题或建议，欢迎给我们留言
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-4"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  您的称呼 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入您的称呼"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">不超过500字</p>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && formError && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}